import React, { useEffect, useState } from 'react';
import { Button, Card, DatePicker, Form, message, Radio, Table } from 'antd';
import moment from 'moment';

import EnrollmentTableColumn from '../../../config/enrollmentTableColumn';
import tableService from '../../../services/tableService';
import formService from '../../../services/formService';
import dataTransform from '../../../util/dataTransform';
import Global from '../../../util/global';
import { dateFormat } from '../../../util/util';
import LocalStorageService from '../../../services/localStorageService';
import TeacherFormItem from '../../components/form/TeacherFormItem';
import BrForForm from '../../components/form/BrForForm';
import Layout from '../../components/form/layout';

const { RangePicker } = DatePicker;

const displayEnrollment = LocalStorageService.isAdmin ? 90 : 300;
const defaultInitialValue = [
  moment(moment().subtract(displayEnrollment, 'days'), dateFormat),
  moment(moment(), dateFormat),
];

const isRenewalCourse = (row) => Number(row.reEnroll || 0) === 1;

const hasNoRemainingLessons = (row) =>
  row.remainingLessons !== '' &&
  row.remainingLessons !== null &&
  row.remainingLessons !== undefined &&
  Number(row.remainingLessons) === 0;

const parseDate = (date) => {
  if (!date) {
    return null;
  }
  if (moment.isMoment(date)) {
    return date;
  }
  return moment(date, [dateFormat, 'YYYY-MM-DD', moment.ISO_8601], true);
};

const isDateInRange = (date, rangeDatePick, includeWhenNoRange = false) => {
  const target = parseDate(date);
  if (!target || !target.isValid()) {
    return false;
  }
  if (!rangeDatePick || !rangeDatePick[0] || !rangeDatePick[1]) {
    return includeWhenNoRange;
  }
  const from = parseDate(rangeDatePick[0]);
  const to = parseDate(rangeDatePick[1]);
  if (!from || !to || !from.isValid() || !to.isValid()) {
    return false;
  }
  return target.isBetween(from, to, 'day', '[]');
};

const formatRate = (value) => {
  if (!Number.isFinite(value)) {
    return '0%';
  }
  return `${(value * 100).toFixed(1)}%`;
};

const CourseRenewalTablePage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [teacherList, setTeacherList] = useState([]);
  const [closingData, setClosingData] = useState([]);
  const [renewalData, setRenewalData] = useState([]);
  const [radioValue, setRadioValue] = useState(Global.last90);
  const [selectedRange, setSelectedRange] = useState(defaultInitialValue);

  const columns =
    LocalStorageService.getCurrentRole() === 2
      ? EnrollmentTableColumn.columns_user
      : EnrollmentTableColumn.columns_admin_core;

  const closingStudentCount = closingData.length;
  const renewalStudentCount = renewalData.length;
  const renewalRate =
    closingStudentCount > 0 ? renewalStudentCount / closingStudentCount : 0;
  const renewalWeightTotal = renewalData.reduce(
    (sum, item) => sum + Number(item.courseWeight || 0),
    0
  );
  const renewalWeightedRate =
    closingStudentCount > 0 ? renewalWeightTotal / closingStudentCount / 2 : 0;

  const fetchTeacherList = async () => {
    const res = await formService.queryTeacher();
    if (res.data) {
      setTeacherList(res.data);
    }
  };

  const getQuery = () => {
    const { teacher } = form.getFieldValue();
    return teacher ? `&teacher=${teacher}` : '';
  };

  const fetchData = async (rangeDatePick) => {
    setLoading(true);
    try {
      const activeRange =
        rangeDatePick !== undefined ? rangeDatePick : selectedRange;
      const res = await tableService.getEnrollments(1, 2000, getQuery());
      const { displayData } = dataTransform.enrollmentListDataTransform(
        res.data
      );
      setClosingData(
        displayData.filter(
          (item) =>
            isDateInRange(item.finishDate, activeRange, true) ||
            (hasNoRemainingLessons(item) &&
              isDateInRange(item.lastActiveDate, activeRange, true))
        )
      );
      setRenewalData(
        displayData.filter(
          (item) =>
            isRenewalCourse(item) &&
            isDateInRange(item.enrollDate, activeRange, true)
        )
      );
    } catch (error) {
      message.error('获取结课续课表格失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRangeButtonChange = (e) => {
    form.setFieldsValue({ rangeDatePick: e.target.value });
    setSelectedRange(e.target.value);
    setRadioValue(e.target.value);
    fetchData(e.target.value);
  };

  const handleReset = () => {
    form.resetFields();
    form.setFieldsValue({ rangeDatePick: defaultInitialValue });
    setSelectedRange(defaultInitialValue);
    setRadioValue(Global.last90);
    fetchData(defaultInitialValue);
  };

  useEffect(() => {
    form.setFieldsValue({ rangeDatePick: defaultInitialValue });
    fetchTeacherList();
    fetchData();
  }, []);

  return (
    <div>
      <Card style={{ marginTop: 10 }}>
        <Form
          layout='inline'
          form={form}
          initialValues={{ rangeDatePick: defaultInitialValue }}
        >
          <TeacherFormItem
            formItemLayout={{}}
            rules={[]}
            form={form}
            teacherList={teacherList}
            fetchTeacherList={fetchTeacherList}
            itemStyle={{ width: 180 }}
          />
          <Form.Item label='选择日期' {...Layout.filterInputStyle}>
            <RangePicker
              allowClear
              value={selectedRange}
              onChange={(dates) => {
                const nextRange = dates || [];
                setSelectedRange(nextRange);
                form.setFieldsValue({ rangeDatePick: nextRange });
                setRadioValue(null);
              }}
            />
          </Form.Item>
          <BrForForm />
          <Form.Item>
            <Radio.Group
              value={radioValue}
              onChange={(e) => handleRangeButtonChange(e)}
            >
              <Radio.Button value={Global.today}>今日</Radio.Button>
              <Radio.Button value={Global.lastMonth}>上月</Radio.Button>
              <Radio.Button value={Global.thisMonth}>本月</Radio.Button>
              <Radio.Button value={Global.last90}>季度</Radio.Button>
              <Radio.Button value={Global.thisYear}>本年</Radio.Button>
              <Radio.Button value={Global.all}>全部</Radio.Button>
            </Radio.Group>
            <Button
              type='primary'
              style={{ margin: '0 20px' }}
              onClick={() => fetchData(selectedRange)}
            >
              查询
            </Button>
            <Button htmlType='button' onClick={handleReset}>
              重置
            </Button>
          </Form.Item>
        </Form>
      </Card>
      <Card title='续课率统计' style={{ marginTop: 10 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
            gap: 16,
          }}
        >
          <div>
            <div style={{ color: '#666', marginBottom: 6 }}>结课学生数</div>
            <div style={{ fontSize: 20 }}>{closingStudentCount}</div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: 6 }}>续课学生数</div>
            <div style={{ fontSize: 20 }}>{renewalStudentCount}</div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: 6 }}>续课率</div>
            <div style={{ fontSize: 20 }}>{formatRate(renewalRate)}</div>
          </div>
          <div>
            <div style={{ color: '#666', marginBottom: 6 }}>续课率加权</div>
            <div style={{ fontSize: 20 }}>{formatRate(renewalWeightedRate)}</div>
          </div>
        </div>
      </Card>
      <Card title='结课学生' style={{ marginTop: 10 }}>
        <Table
          loading={loading}
          dataSource={closingData}
          columns={columns}
          pagination={{ position: ['topLeft'], pageSize: 100 }}
        />
      </Card>
      <Card title='续课学生' style={{ marginTop: 10 }}>
        <Table
          loading={loading}
          dataSource={renewalData}
          columns={columns}
          pagination={{ position: ['topLeft'], pageSize: 100 }}
        />
      </Card>
    </div>
  );
};

export default CourseRenewalTablePage;
